(() => {
  'use strict';

  const qs = (root, selector) => root ? root.querySelector(selector) : null;
  const qsa = (root, selector) => Array.from(root ? root.querySelectorAll(selector) : []);
  const isMobile = () => window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  const NATIVE_STATES = ['opening', 'open', 'closing'];

  function closestProductInfo(node) {
    return node && (node.closest('product-info') || document.querySelector('product-info'));
  }

  function findStudio(productInfo) {
    return qs(productInfo, '.studio.studio--pdp[data-studio]');
  }

  function activeNativeSwitch() {
    return qs(document, '[data-pdp-studio-switch][data-pdp-studio-native-active="true"]');
  }

  function nativeState(switchEl) {
    return switchEl && switchEl.dataset.pdpStudioNativeState ? switchEl.dataset.pdpStudioNativeState : 'closed';
  }

  function setSwitchState(switchEl, mode) {
    qsa(switchEl, '[data-pdp-studio-mode]').forEach((button) => {
      const active = button.getAttribute('data-pdp-studio-mode') === mode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function mountNativeSwitch(switchEl, active) {
    if (!switchEl) return;
    const productInfo = closestProductInfo(switchEl);
    const studio = findStudio(productInfo);

    if (active && isMobile() && studio) {
      const body = qs(studio, '.studio__body');
      if (!body) return;
      if (!switchEl.__pdpStudioPlaceholder && switchEl.parentNode) {
        switchEl.__pdpStudioPlaceholder = document.createComment('pdp-studio-switch-placeholder');
        switchEl.parentNode.insertBefore(switchEl.__pdpStudioPlaceholder, switchEl);
      }
      if (switchEl.parentNode !== body) body.insertBefore(switchEl, body.firstChild);
      switchEl.dataset.pdpStudioMounted = 'studio';
      return;
    }

    const placeholder = switchEl.__pdpStudioPlaceholder;
    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.insertBefore(switchEl, placeholder);
      placeholder.parentNode.removeChild(placeholder);
      switchEl.__pdpStudioPlaceholder = null;
    }
    switchEl.removeAttribute('data-pdp-studio-mounted');
  }

  function setHeaderCloseState(active) {
    qsa(document, '#hamburger-button, header-drawer .hamburger').forEach((hamburger) => {
      hamburger.classList.toggle('active', !!active);
      hamburger.setAttribute('aria-label', active ? 'Close B¹ Studio' : 'Menu');
    });

    qsa(document, '#Details-menu-drawer-container > summary.header__icon--menu, header-drawer summary.header__icon--menu, header .header__icon--menu').forEach((summary) => {
      summary.setAttribute('aria-expanded', active ? 'true' : 'false');
    });
  }

  function closeNativeMenuSurfaces() {
    qsa(document, '#Details-menu-drawer-container, header-drawer details').forEach((details) => {
      details.classList.remove('menu-opening');
      details.removeAttribute('open');
    });
  }

  function syncNativeHeaderOffset(active) {
    if (!active || !isMobile()) {
      document.documentElement.style.removeProperty('--pdp-native-header-offset');
      return;
    }

    const header = qs(document, '#shopify-section-header .header-wrapper') || qs(document, '.section-header .header-wrapper') || qs(document, '.header-wrapper') || qs(document, '#shopify-section-header') || qs(document, '.section-header');
    const rect = header && header.getBoundingClientRect ? header.getBoundingClientRect() : null;
    const height = rect && rect.height ? Math.max(48, Math.round(rect.height)) : 58;
    document.documentElement.style.setProperty('--pdp-native-header-offset', `${height}px`);
  }

  function setDocumentNativeState(state) {
    const active = NATIVE_STATES.includes(state);
    document.documentElement.classList.toggle('pdp-studio-native-mode', active);
    document.body.classList.toggle('pdp-studio-native-mode', active);

    ['opening', 'open', 'closing'].forEach((name) => {
      const on = state === name;
      document.documentElement.classList.toggle(`pdp-studio-native-${name}`, on);
      document.body.classList.toggle(`pdp-studio-native-${name}`, on);
    });

    if (active) {
      document.documentElement.dataset.pdpStudioNativeState = state;
      document.body.dataset.pdpStudioNativeState = state;
    } else {
      document.documentElement.removeAttribute('data-pdp-studio-native-state');
      document.body.removeAttribute('data-pdp-studio-native-state');
    }
  }

  function setNativeState(switchEl, state) {
    if (!isMobile()) state = 'closed';
    const active = NATIVE_STATES.includes(state);

    if (switchEl) {
      switchEl.dataset.pdpStudioNativeActive = active ? 'true' : 'false';
      if (active) switchEl.dataset.pdpStudioNativeState = state;
      else switchEl.removeAttribute('data-pdp-studio-native-state');
    }

    if (active) {
      closeNativeMenuSurfaces();
      syncNativeHeaderOffset(true);
      mountNativeSwitch(switchEl, true);
      setHeaderCloseState(true);
    } else {
      mountNativeSwitch(switchEl, false);
      syncNativeHeaderOffset(false);
      setHeaderCloseState(false);
    }

    setDocumentNativeState(state);
  }

  function openStudio(productInfo) {
    const studio = findStudio(productInfo);
    const trigger = qs(studio, '[data-studio-trigger]');
    if (!studio || !trigger) return false;
    if (studio.getAttribute('data-studio-state') !== 'open') {
      trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    }
    return true;
  }

  function closeStudio(productInfo) {
    const studio = findStudio(productInfo);
    const close = qs(studio, '[data-studio-close]');
    if (!studio) return false;
    if (studio.getAttribute('data-studio-state') === 'open' && close) {
      close.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      return true;
    }
    return false;
  }

  function clearPendingTimers(switchEl) {
    if (!switchEl) return;
    if (switchEl.__pdpNativeOpenTimer) {
      window.clearTimeout(switchEl.__pdpNativeOpenTimer);
      switchEl.__pdpNativeOpenTimer = null;
    }
    if (switchEl.__pdpNativeCloseTimer) {
      window.clearTimeout(switchEl.__pdpNativeCloseTimer);
      switchEl.__pdpNativeCloseTimer = null;
    }
    if (switchEl.__pdpNativeCloseObserver) {
      switchEl.__pdpNativeCloseObserver.disconnect();
      switchEl.__pdpNativeCloseObserver = null;
    }
  }

  function finalizeNativeClose(switchEl) {
    clearPendingTimers(switchEl);
    setSwitchState(switchEl, 'gallery');
    setNativeState(switchEl, 'closed');
  }

  function scheduleNativeOpenSettled(switchEl) {
    if (!switchEl) return;
    if (switchEl.__pdpNativeOpenTimer) window.clearTimeout(switchEl.__pdpNativeOpenTimer);
    switchEl.__pdpNativeOpenTimer = window.setTimeout(() => {
      switchEl.__pdpNativeOpenTimer = null;
      const productInfo = closestProductInfo(switchEl);
      const studio = findStudio(productInfo);
      if (studio && studio.getAttribute('data-studio-state') === 'open' && nativeState(switchEl) !== 'closing') {
        setNativeState(switchEl, 'open');
      }
    }, 90);
  }

  function waitForStudioClosed(switchEl) {
    const productInfo = closestProductInfo(switchEl);
    const studio = findStudio(productInfo);
    if (!studio || studio.getAttribute('data-studio-state') !== 'open') {
      finalizeNativeClose(switchEl);
      return;
    }

    if (switchEl.__pdpNativeCloseObserver) switchEl.__pdpNativeCloseObserver.disconnect();
    switchEl.__pdpNativeCloseObserver = new MutationObserver(() => {
      if (studio.getAttribute('data-studio-state') !== 'open') finalizeNativeClose(switchEl);
    });
    switchEl.__pdpNativeCloseObserver.observe(studio, { attributes: true, attributeFilter: ['data-studio-state'] });

    if (switchEl.__pdpNativeCloseTimer) window.clearTimeout(switchEl.__pdpNativeCloseTimer);
    switchEl.__pdpNativeCloseTimer = window.setTimeout(() => {
      finalizeNativeClose(switchEl);
    }, 320);
  }

  function requestNativeOpen(switchEl, productInfo) {
    clearPendingTimers(switchEl);
    setNativeState(switchEl, 'opening');
    const didOpen = openStudio(productInfo);
    if (!didOpen) {
      finalizeNativeClose(switchEl);
      return false;
    }
    setSwitchState(switchEl, 'studio');
    scheduleNativeOpenSettled(switchEl);
    return true;
  }

  function requestNativeClose(switchEl) {
    if (!switchEl) return false;
    const productInfo = closestProductInfo(switchEl);
    const studio = findStudio(productInfo);
    if (!studio) {
      finalizeNativeClose(switchEl);
      return false;
    }

    if (nativeState(switchEl) === 'closing') return true;
    if (switchEl.__pdpNativeOpenTimer) {
      window.clearTimeout(switchEl.__pdpNativeOpenTimer);
      switchEl.__pdpNativeOpenTimer = null;
    }

    setSwitchState(switchEl, 'gallery');
    setNativeState(switchEl, 'closing');
    const didClose = closeStudio(productInfo);
    if (!didClose) finalizeNativeClose(switchEl);
    else waitForStudioClosed(switchEl);
    return true;
  }


  function activeCartDrawer() {
    const drawer = qs(document, 'cart-drawer.drawer.active, cart-drawer.active');
    return drawer || null;
  }

  function closeActiveCartDrawer() {
    const drawer = activeCartDrawer();
    if (!drawer) return false;
    if (typeof drawer.close === 'function') drawer.close();
    else drawer.classList.remove('active');
    if (document.body.classList.contains('pdp-studio-native-mode')) {
      window.requestAnimationFrame(() => setHeaderCloseState(true));
    }
    return true;
  }

  function closeNativeStudioFromHeader() {
    const switchEl = activeNativeSwitch();
    if (!switchEl) return false;
    return requestNativeClose(switchEl);
  }

  function bindSwitch(switchEl) {
    if (!switchEl || switchEl.dataset.pdpStudioSwitchBound === 'true') return;
    switchEl.dataset.pdpStudioSwitchBound = 'true';
    const productInfo = closestProductInfo(switchEl);
    const studio = findStudio(productInfo);
    if (!productInfo || !studio) {
      switchEl.hidden = true;
      return;
    }

    switchEl.addEventListener('click', (event) => {
      const button = event.target.closest('[data-pdp-studio-mode]');
      if (!button) return;
      const mode = button.getAttribute('data-pdp-studio-mode');
      if (mode === 'studio') {
        requestNativeOpen(switchEl, productInfo);
      } else {
        requestNativeClose(switchEl);
      }
    });

    const sync = () => {
      if (!isMobile()) {
        finalizeNativeClose(switchEl);
        return;
      }

      const open = studio.getAttribute('data-studio-state') === 'open';
      const state = nativeState(switchEl);

      if (!open) {
        if (state !== 'closed') finalizeNativeClose(switchEl);
        else setSwitchState(switchEl, 'gallery');
        return;
      }

      if (state === 'opening') {
        setSwitchState(switchEl, 'studio');
        scheduleNativeOpenSettled(switchEl);
        return;
      }

      if (state === 'open') {
        setSwitchState(switchEl, 'studio');
        syncNativeHeaderOffset(true);
        return;
      }

      if (state === 'closing') {
        setSwitchState(switchEl, 'gallery');
        waitForStudioClosed(switchEl);
        return;
      }

      /* If a PDP Studio is already open when the viewport crosses into
         mobile, promote it into the native PDP Studio state. This prevents
         the legacy internal Studio header from appearing after DevTools /
         responsive breakpoint changes without requiring a page refresh. */
      setSwitchState(switchEl, 'studio');
      setNativeState(switchEl, 'open');
    };

    const observer = new MutationObserver(sync);
    observer.observe(studio, { attributes: true, attributeFilter: ['data-studio-state'] });
    sync();
  }

  function bindHeaderClose() {
    if (document.documentElement.dataset.pdpStudioHeaderCloseBound === 'true') return;
    document.documentElement.dataset.pdpStudioHeaderCloseBound = 'true';

    document.addEventListener('click', (event) => {
      if (!document.body.classList.contains('pdp-studio-native-mode')) return;
      const headerClose = event.target && event.target.closest ? event.target.closest('#hamburger-button, header-drawer .hamburger, #Details-menu-drawer-container > summary.header__icon--menu, header-drawer summary.header__icon--menu, header .header__icon--menu') : null;
      if (!headerClose) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      if (closeActiveCartDrawer()) return;
      closeNativeStudioFromHeader();
    }, true);

    window.addEventListener('resize', () => {
      const mobile = isMobile();
      qsa(document, '[data-pdp-studio-switch]').forEach((switchEl) => {
        if (!mobile) {
          finalizeNativeClose(switchEl);
          return;
        }

        const productInfo = closestProductInfo(switchEl);
        const studio = findStudio(productInfo);
        if (studio && studio.getAttribute('data-studio-state') === 'open') {
          setSwitchState(switchEl, 'studio');
          setNativeState(switchEl, 'open');
        } else if (nativeState(switchEl) !== 'closed') {
          finalizeNativeClose(switchEl);
        }
      });

      if (document.body.classList.contains('pdp-studio-native-mode')) syncNativeHeaderOffset(true);
    });
  }

  function init() {
    qsa(document, '[data-pdp-studio-switch]').forEach(bindSwitch);
    bindHeaderClose();
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
